import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Truck, MapPin, ArrowLeft, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PhotoBig = ({ fileId, className }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/trailer-hire/files/${fileId}`).then(r => {
      if (!cancelled) setSrc(r.data?.file_data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [fileId]);
  if (!src) return <div className={`${className} bg-gray-100 animate-pulse`} />;
  return <img src={src} alt="trailer" className={className} />;
};

const Pill = ({ active, children }) => active ? (
  <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800 border border-green-300">
    {children}
  </span>
) : null;

const TrailerHireDetail = () => {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBookForm, setShowBookForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    hirer_name: '',
    hirer_email: localStorage.getItem('user_email') || '',
    hirer_phone: '',
    hirer_company_name: '',
    start_date: '',
    end_date: '',
    notes: ''
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/trailer-hire/listings/${listingId}`);
        setListing(res.data);
      } catch {
        toast.error('Listing not found');
      } finally {
        setLoading(false);
      }
    })();
  }, [listingId]);

  const set = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));

  const submitBooking = async () => {
    const required = ['hirer_name', 'hirer_email', 'hirer_phone', 'start_date', 'end_date'];
    const missing = required.filter(f => !form[f]);
    if (missing.length) { toast.error(`Please fill: ${missing.join(', ')}`); return; }
    if (new Date(form.end_date) < new Date(form.start_date)) { toast.error('End date must be after start date'); return; }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/trailer-hire/bookings`, {
        listing_id: listingId,
        ...form
      });
      setSubmitted(true);
      toast.success('Booking request sent — the operator will reply with a rate');
      if (res.data?.insurance_status === 'pending_upload') {
        toast.info('You\'ll need to upload Trailers in Control insurance after accepting the rate', { duration: 6000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Booking failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-gray-50"><Header /><div className="container mx-auto px-4 py-16 text-center text-gray-500">Loading…</div></div>;
  if (!listing) return <div className="min-h-screen bg-gray-50"><Header /><div className="container mx-auto px-4 py-16 text-center">Listing not found.</div></div>;

  const checkboxes = [
    ['road_train_rated', 'Road Train Rated'],
    ['mez_deck', 'Mez Deck'],
    ['deck_widener', 'Deck Widener'],
    ['extendable', 'Extendable'],
    ['walking', 'Walking Floor'],
    ['dg_rated', 'DG Rated'],
    ['tail_gate_lift', 'Tail Gate Lift'],
    ['ramps', 'Ramps'],
    ['beaver_tail', 'Beaver Tail'],
    ['wa_period_permit', 'WA Period Permit']
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <Button variant="ghost" onClick={() => navigate('/trailer-hire')} className="mb-3" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to listings
        </Button>

        <Card className="p-6">
          <div className="flex items-start gap-2 mb-4">
            <Truck className="w-7 h-7 text-red-500" />
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[10px] tracking-wider font-bold uppercase px-2 py-0.5 rounded ${listing.vehicle_category === 'truck' ? 'bg-amber-100 text-amber-800 border border-amber-300' : 'bg-blue-100 text-blue-800 border border-blue-300'}`}>
                  {listing.vehicle_category === 'truck' ? 'Truck' : 'Trailer'}
                </span>
              </div>
              <h1 className="text-2xl font-bold text-[#1e3a5f]">
                {listing.vehicle_category === 'truck'
                  ? (listing.truck_type === 'Other' ? listing.truck_type_other : listing.truck_type) || 'Truck'
                  : listing.trailer_type || 'Trailer'}
              </h1>
              <p className="text-sm text-gray-600 flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" /> {listing.base_location}
              </p>
            </div>
          </div>

          {/* Photo gallery */}
          {listing.photo_ids?.length > 0 && (
            <div className="grid sm:grid-cols-3 gap-3 mb-5">
              {listing.photo_ids.map((fid, i) => (
                <PhotoBig key={fid} fileId={fid} className="w-full h-48 object-cover rounded border" />
              ))}
            </div>
          )}

          {/* Specs */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
            <Spec label="Axles" value={listing.number_of_axles} />
            <Spec label="Tyres / Axle" value={listing.tyres_per_axle} />
            <Spec label="GVM" value={listing.gvm} />
            <Spec label="GCM" value={listing.gcm} />
            <Spec label="Avg Payload" value={listing.average_payload} />
            {listing.vehicle_category === 'truck' ? (
              <>
                <Spec label="Pallet Spaces" value={listing.pallet_spaces} />
                {listing.truck_type === 'Prime Mover' && (
                  <>
                    <Spec label="Road Train Rated" value={listing.road_train_rated_truck ? 'Yes' : 'No'} />
                    <Spec label="Turntable" value={listing.turntable_size} />
                  </>
                )}
                {(listing.truck_type === 'Rigid Tautliner' || listing.truck_type === 'Rigid Pantech') && (
                  <Spec
                    label="Tailgate"
                    value={listing.has_tailgate
                      ? `Yes${listing.tailgate_weight_limit ? ' — ' + listing.tailgate_weight_limit : ''}`
                      : 'No'}
                  />
                )}
                {(listing.truck_type === 'Rigid Tipper' || listing.truck_type === 'Rigid Flat Top') && (
                  <Spec label="Ramps" value={listing.has_ramps ? 'Yes' : 'No'} />
                )}
              </>
            ) : (
              <>
                <Spec label="Deck Length" value={listing.deck_length} />
                <Spec label="Coupling" value={listing.coupling_type} />
                <Spec label="Axle Spacing" value={listing.axle_spacing === 'other' ? listing.axle_spacing_other : (listing.axle_spacing || '—')} />
              </>
            )}
            {listing.wa_period_permit && (
              <Spec label="WA Period Permit" value={listing.wa_period_permit_details || 'Yes'} />
            )}
          </div>

          {/* Feature pills — trailer only */}
          {listing.vehicle_category !== 'truck' && (
            <div className="flex flex-wrap gap-1.5 mb-5">
              {checkboxes.map(([k, label]) => (
                <Pill key={k} active={listing[k]}>{label}</Pill>
              ))}
            </div>
          )}

          {listing.notes && (
            <p className="text-sm text-gray-600 italic mb-5">{listing.notes}</p>
          )}

          {!showBookForm && !submitted && (
            <Button
              size="lg"
              className="bg-red-600 hover:bg-red-700 text-white w-full sm:w-auto"
              onClick={() => setShowBookForm(true)}
              data-testid="book-now-btn"
            >
              Book Now
            </Button>
          )}

          {submitted && (
            <div className="bg-green-50 border border-green-200 rounded p-4 text-green-800">
              <CheckCircle2 className="w-5 h-5 inline mr-2" />
              Booking request sent. The operator will respond with a rate. You&apos;ll get an email when they reply.
            </div>
          )}

          {showBookForm && !submitted && (
            <Card className="p-4 mt-5 bg-blue-50 border-blue-200">
              <h2 className="text-lg font-bold text-[#1e3a5f] mb-2">Request these dates</h2>
              <p className="text-xs text-gray-600 mb-4">
                Submit your dates; the operator will confirm availability and quote a rate (per day, week or month).
                If you accept, you&apos;ll be asked to upload <strong>Trailers in Control insurance</strong> (unless you&apos;re already a registered carrier with it) then pay via Stripe.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Your name *</Label><Input value={form.hirer_name} onChange={set('hirer_name')} data-testid="hirer-name" /></div>
                <div><Label>Phone *</Label><Input value={form.hirer_phone} onChange={set('hirer_phone')} data-testid="hirer-phone" /></div>
                <div><Label>Email *</Label><Input type="email" value={form.hirer_email} onChange={set('hirer_email')} data-testid="hirer-email" /></div>
                <div><Label>Company</Label><Input value={form.hirer_company_name} onChange={set('hirer_company_name')} data-testid="hirer-company" /></div>
                <div><Label>Start date *</Label><Input type="date" value={form.start_date} onChange={set('start_date')} data-testid="start-date" /></div>
                <div><Label>End date *</Label><Input type="date" value={form.end_date} onChange={set('end_date')} data-testid="end-date" /></div>
                <div className="sm:col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={set('notes')} placeholder="Anything the operator should know" data-testid="hire-notes" /></div>
              </div>
              <Button
                onClick={submitBooking}
                disabled={submitting}
                className="mt-4 w-full bg-red-600 hover:bg-red-700"
                data-testid="submit-booking"
              >
                {submitting ? 'Sending…' : 'Send booking request'}
              </Button>
            </Card>
          )}
        </Card>
      </div>

      <Footer />
    </div>
  );
};

const Spec = ({ label, value }) => (
  <div className="text-sm">
    <span className="text-gray-500">{label}:</span>{' '}
    <span className="font-medium text-gray-800">{value || '—'}</span>
  </div>
);

export default TrailerHireDetail;
